// MultiSelectFacet — checkbox list with optional bucket counts.
// Emits via onChange(nextValueArray). Sorted by count desc when counts known.
//
// Keyboard: focusable list of checkboxes; space toggles; arrow keys move
// focus between options (browser default within a fieldset).

import { useMemo } from 'react';

export interface FacetOption {
  value: string;
  label?: string;
  count?: number;
}

export interface MultiSelectFacetProps {
  field: string;
  label: string;
  options: FacetOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Limit the visible options. "MORE" toggle expands to all. */
  collapseAfter?: number;
  /** Host-provided enum value → display label. Falls back to opt.label / opt.value. */
  formatOption?: (value: string) => string | undefined;
  /** Localized chrome labels. Host wires t() to keep i18n out of the platform. */
  chrome?: { clr?: string; more?: string };
}

export function MultiSelectFacet({
  field,
  label,
  options,
  value,
  onChange,
  collapseAfter = 6,
  formatOption,
  chrome,
}: MultiSelectFacetProps) {
  const clr = chrome?.clr ?? 'CLR';
  const moreTpl = chrome?.more ?? 'MORE ({{count}})';
  const sorted = useMemo(() => {
    const labelOf = (o: FacetOption) => formatOption?.(o.value) ?? o.label ?? o.value;
    const cmp = (a: FacetOption, b: FacetOption) =>
      (b.count ?? 0) - (a.count ?? 0) || labelOf(a).localeCompare(labelOf(b));
    return [...options].sort(cmp);
  }, [options, formatOption]);

  const selected = new Set(value);
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange([...next]);
  };

  return (
    <fieldset
      data-facet={field}
      style={{
        border: 'none',
        borderTop: '1px solid var(--bd)',
        margin: 0,
        padding: '8px 10px 10px',
      }}
    >
      <legend
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 0,
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        <span>{label}</span>
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--fg-3)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
            }}
            title={`clear ${label}`}
          >
            {clr}
          </button>
        )}
      </legend>
      <div role="group" style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sorted.slice(0, collapseAfter).map((opt) => (
          <FacetRow
            key={opt.value}
            label={formatOption?.(opt.value) ?? opt.label ?? opt.value}
            count={opt.count}
            checked={selected.has(opt.value)}
            onToggle={() => toggle(opt.value)}
          />
        ))}
        {sorted.length > collapseAfter && (
          <details>
            <summary
              style={{
                cursor: 'pointer',
                color: 'var(--fg-3)',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                listStyle: 'none',
                padding: '2px 0',
              }}
            >
              {moreTpl.replace('{{count}}', String(sorted.length - collapseAfter))}
            </summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sorted.slice(collapseAfter).map((opt) => (
                <FacetRow
                  key={opt.value}
                  label={formatOption?.(opt.value) ?? opt.label ?? opt.value}
                  count={opt.count}
                  checked={selected.has(opt.value)}
                  onToggle={() => toggle(opt.value)}
                />
              ))}
            </div>
          </details>
        )}
      </div>
    </fieldset>
  );
}

function FacetRow({
  label,
  count,
  checked,
  onToggle,
}: {
  label: string;
  count: number | undefined;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        height: 20,
        cursor: 'pointer',
        padding: '0 2px',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          style={{ accentColor: 'var(--primary)' }}
        />
        <span
          style={{
            color: 'var(--fg-1)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </span>
      {count !== undefined && (
        <span
          style={{
            color: 'var(--fg-3)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count.toLocaleString()}
        </span>
      )}
    </label>
  );
}
